(() => {
  'use strict';

  const STORAGE_KEY = 'yuropet-inquiry-list-v3';
  // Temporary contact details belong to the current browser tab, not the
  // long-lived product shortlist or any other site contact form.
  const QUOTE_DRAFT_KEY = 'yuropet-quote-details-draft-v1';
  const QUOTE_DRAFT_TTL = 2 * 60 * 60 * 1000;
  const inquiryUrl = () => {
    const url = new URL('/pages/request-a-quote', window.location.origin);
    url.searchParams.set('view', 'list');
    // Keep the unpublished theme preview when entering the list from elsewhere.
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search;
  };
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  const dateStamp = () => {
    const date = new Date();
    return `${String(date.getFullYear()).slice(-2)}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  };

  // A blocked read must NOT be interpreted as an empty list and overwritten.
  // Failed writes are reported, never claimed as successful.
  let storageIssue = '';
  const readItems = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const current = raw === null ? [] : JSON.parse(raw);
      if (!Array.isArray(current)) throw new Error('Invalid inquiry list data');
      if (storageIssue === 'read') storageIssue = '';
      return current;
    } catch (_error) {
      storageIssue = 'read';
      return [];
    }
  };
  const writeItems = (items, notify = true) => {
    if (storageIssue === 'read') return false;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      storageIssue = '';
      if (notify) document.dispatchEvent(new CustomEvent('yuropet:inquiry-updated'));
      return true;
    } catch (_error) {
      storageIssue = 'write';
      return false;
    }
  };
  const showStorageWarning = (container) => {
    if (!container) return;
    let warning = container.querySelector('[data-yp-inquiry-storage-warning]');
    if (!storageIssue) {
      warning?.remove();
      return;
    }
    if (warning) return;
    warning = document.createElement('p');
    warning.className = 'yp-inquiry-storage-warning';
    warning.dataset.ypInquiryStorageWarning = '';
    warning.setAttribute('role', 'alert');
    warning.innerHTML = 'We couldn\'t access or save your inquiry list in this browser. Your selections or edits may not be saved. Please check browser storage settings, or <a href="/pages/contact">contact us directly</a> with your product details.';
    container.prepend(warning);
  };
  const selectedOptions = (root) => [...root.querySelectorAll('[data-yp-option]')]
    .filter((button) => button.classList.contains('is-active') || button.getAttribute('aria-pressed') === 'true')
    .reduce((selection, button) => {
      const name = button.dataset.ypOption;
      if (name && selection[name] == null) selection[name] = button.dataset.ypOptionValue;
      return selection;
    }, {});
  const variantData = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-yp-variants]')?.textContent || '{"variants":[]}');
    } catch (_error) {
      return { variants: [] };
    }
  };
  const selectedVariant = (root, options) => {
    const data = variantData(root);
    return data.variants?.find((variant) => data.optionNames.every((name, index) => variant.options[index] === options[name])) || data.variants?.[0] || {};
  };
  const productReference = (root, variant) => {
    if (root.dataset.ypProductReference && !/^(reference pending|pending|n\/a)$/i.test(root.dataset.ypProductReference.trim())) return root.dataset.ypProductReference;
    const sku = variant.sku || '';
    const hidream = sku.match(/^(HD\d{6})-/i);
    if (hidream) return hidream[1].toUpperCase();
    const yuropet = sku.match(/^(YP-[A-Z]+-\d+)/i);
    if (yuropet) return yuropet[1].toUpperCase();
    return ''; // An unpublished internal reference is not a buyer-facing product name.
  };
  const choiceLabel = (options) => {
    const color = Object.entries(options).find(([name]) => /colou?r/i.test(name))?.[1];
    const size = Object.entries(options).find(([name]) => /^size$/i.test(name))?.[1];
    const other = Object.entries(options)
      .filter(([name]) => !/colou?r|^size$/i.test(name))
      .map(([, value]) => value);
    return [color, size, ...other].filter(Boolean).join(' · ');
  };
  const itemId = (root, variant, options, note, customization) => [
    window.location.pathname,
    variant.sku || '',
    JSON.stringify(options),
    note.trim(),
    customization.join('|')
  ].join('|');
  const refreshCount = () => {
    // Header count is number of selected lines, not the combined order quantity.
    const items = readItems();
    const count = items.length;
    const total = items.reduce((sum, item) => sum + Math.max(1, Math.floor(Number(item.quantity || 1))), 0);
    document.querySelectorAll('[data-yp-quote-selected-label]').forEach((element) => {
      element.textContent = count
        ? `${count} ${count === 1 ? 'selected style' : 'selected styles'} · ${total} pcs across the list`
        : 'No selected products yet — describe your request below.';
    });
    document.querySelectorAll('[data-yp-quote-review-count]').forEach((element) => {
      element.textContent = count
        ? `${count} ${count === 1 ? 'style' : 'styles'} · ${total} pcs selected`
        : 'General product inquiry';
    });
    document.querySelectorAll('[data-yp-quote-general-fields]').forEach((section) => {
      const show = count === 0;
      section.hidden = !show;
      section.querySelectorAll('[data-yp-quote-category], [data-yp-quote-general-quantity]').forEach((field) => {
        field.required = show;
        field.disabled = !show;
      });
    });
    document.querySelectorAll('[data-yp-inquiry-count]').forEach((element) => {
      element.textContent = count ? ` (${count})` : '';
      element.hidden = !count;
    });
    document.querySelectorAll('[data-yp-inquiry-link]').forEach((link) => { link.href = inquiryUrl(); });
  };
  const addItem = (root, note, customization, quantityOverride) => {
    const options = selectedOptions(root);
    const variant = selectedVariant(root, options);
    const quantity = Math.max(1, Math.floor(Number(quantityOverride ?? root.querySelector('[data-yp-inquiry-quantity]')?.value ?? 1) || 1));
    const item = {
      id: itemId(root, variant, options, note, customization),
      reference: productReference(root, variant),
      title: root.dataset.ypProductTitle || document.title,
      url: window.location.pathname,
      options,
      sku: variant.sku || '',
      image: variant.image || root.dataset.ypProductImage || '',
      quantity,
      note: note.trim(),
      customization,
      addedAt: new Date().toISOString()
    };
    const items = readItems();
    if (storageIssue === 'read') return false;
    const existing = items.find((candidate) => candidate.id === item.id);
    if (existing) existing.quantity += quantity;
    else items.push(item);
    if (!writeItems(items)) return false;
    const status = root.querySelector('[data-yp-inquiry-status]');
    if (status) {
      status.textContent = 'Added to your list.';
      status.hidden = false;
      window.setTimeout(() => { status.hidden = true; }, 3200);
    }
    return true;
  };
  const closeDialog = (dialog) => {
    const focusReturn = dialog._ypFocusReturn;
    dialog.remove();
    if (focusReturn?.isConnected) focusReturn.focus({ preventScroll: true });
  };
  const openAddDialog = (root) => {
    const options = selectedOptions(root);
    const variant = selectedVariant(root, options);
    const reference = productReference(root, variant);
    const image = variant.image || root.dataset.ypProductImage || '';
    const selection = choiceLabel(options);
    const productName = root.dataset.ypProductTitle || document.title || 'Selected product';
    const initialQuantity = Math.max(1, Math.floor(Number(root.querySelector('[data-yp-inquiry-quantity]')?.value || 1) || 1));
    const dialog = document.createElement('div');
    dialog.className = 'yp-inquiry-dialog-backdrop';
    dialog._ypFocusReturn = document.activeElement;
    dialog.innerHTML = `
      <section class="yp-inquiry-dialog" role="dialog" aria-modal="true" aria-labelledby="yp-inquiry-dialog-title" aria-describedby="yp-inquiry-dialog-help">
        <button type="button" class="yp-inquiry-dialog__close" data-yp-dialog-close aria-label="Close inquiry dialog">×</button>
        <p class="yp-inquiry-dialog__eyebrow">YOUR PRODUCT SHORTLIST</p>
        <h2 id="yp-inquiry-dialog-title">Add to Inquiry List</h2>
        <p id="yp-inquiry-dialog-help" class="yp-inquiry-dialog__intro">Confirm the style and add any details you want us to review.</p>
        <div class="yp-inquiry-dialog__selection">
          ${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : '<span class="yp-inquiry-dialog__image-placeholder" aria-hidden="true"></span>'}
          <div class="yp-inquiry-dialog__product">
            <strong class="yp-inquiry-dialog__product-name">${escapeHtml(productName)}</strong>
            ${selection ? `<span class="yp-inquiry-dialog__variant">${escapeHtml(selection)}</span>` : ''}
            ${reference ? `<span class="yp-inquiry-dialog__ref">Ref: ${escapeHtml(reference)}</span>` : ''}
          </div>
        </div>
        <div class="yp-inquiry-dialog__quantity-row">
          <label for="yp-inquiry-dialog-qty">Estimated quantity</label>
          <div class="yp-inquiry-dialog__quantity-field">
            <input id="yp-inquiry-dialog-qty" data-yp-dialog-quantity type="number" inputmode="numeric" min="1" step="1" value="${initialQuantity}" required>
            <span>pcs</span>
          </div>
        </div>
        <fieldset class="yp-inquiry-dialog__customization">
          <legend>Customization <span>(optional)</span></legend>
          <label><input type="checkbox" name="customization" value="Color"><span>Color</span></label>
          <label><input type="checkbox" name="customization" value="Material"><span>Material</span></label>
          <label><input type="checkbox" name="customization" value="Logo / Label"><span>Logo / Label</span></label>
        </fieldset>
        <label class="yp-inquiry-dialog__notes-label" for="yp-inquiry-dialog-notes">Notes <span>(optional)</span></label>
        <textarea id="yp-inquiry-dialog-notes" rows="3" maxlength="2500" placeholder="Color, logo, packaging or other requests"></textarea>
        <div class="yp-inquiry-dialog__actions">
          <button type="button" class="yp-inquiry-dialog__cancel" data-yp-dialog-close>Cancel</button>
          <button type="button" class="yp-inquiry-dialog__confirm" data-yp-dialog-confirm>Add to List <span aria-hidden="true">→</span></button>
        </div>
      </section>`;
    document.body.append(dialog);
    const notes = dialog.querySelector('#yp-inquiry-dialog-notes');
    const quantityInput = dialog.querySelector('[data-yp-dialog-quantity]');
    dialog.querySelectorAll('[data-yp-dialog-close]').forEach((button) => button.addEventListener('click', () => closeDialog(dialog)));
    dialog.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(dialog); });
    dialog.querySelector('[data-yp-dialog-confirm]').addEventListener('click', () => {
      if (!quantityInput.checkValidity()) {
        quantityInput.reportValidity();
        return;
      }
      const quantity = Math.max(1, Math.floor(Number(quantityInput.value) || 1));
      const customization = [...dialog.querySelectorAll('[name="customization"]:checked')].map((input) => input.value);
      if (!addItem(root, notes.value, customization, quantity)) {
        // Keep the modal and all entered details available for copying.
        let notice = dialog.querySelector('[data-yp-dialog-save-error]');
        if (!notice) {
          notice = document.createElement('div');
          notice.className = 'yp-inquiry-save-error';
          notice.dataset.ypDialogSaveError = '';
          notice.setAttribute('role', 'alert');
          notice.innerHTML = '<strong>We couldn\'t save this selection.</strong><p>Please copy the product details below and <a href="/pages/contact">contact us directly</a>. Avoid leaving this page until you have copied them.</p><textarea readonly aria-label="Copyable inquiry details"></textarea>';
          dialog.querySelector('.yp-inquiry-dialog__actions')?.before(notice);
        }
        notice.querySelector('textarea').value = [
          productName, reference ? 'Ref: ' + reference : '', selection,
          'Qty: ' + quantity,
          customization.length ? 'Customization: ' + customization.join(', ') : '',
          notes.value ? 'Notes: ' + notes.value : '',
          window.location.href
        ].filter(Boolean).join('\n');
        return;
      }
      closeDialog(dialog);
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog(dialog);
      }
      if (event.key === 'Enter' && event.target === quantityInput) {
        event.preventDefault();
        dialog.querySelector('[data-yp-dialog-confirm]')?.click();
      }
      if (event.key === 'Tab') {
        const controls = [...dialog.querySelectorAll('button:not([disabled]),input:not([disabled]),textarea:not([disabled])')];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    });
    dialog.querySelector('[data-yp-dialog-close]')?.focus({ preventScroll: true });
  };
  const decorateProduct = (root) => {
    if (root.dataset.ypInquiryBound === 'true') return;
    root.dataset.ypInquiryBound = 'true';
    root.querySelector('[data-yp-inquiry-add]')?.addEventListener('click', () => openAddDialog(root));
  };
  // Single visual unit per selection: product, chosen attributes, notes and estimated quantity.
  const renderInquiryList = (container) => {
    const items = readItems();
    if (!items.length) {
      container.innerHTML = storageIssue === 'read'
        ? '<div class="yp-inquiry-empty"><h3>Your saved product list is unavailable</h3><p>We cannot safely read the selections stored in this browser. Please check browser storage settings or contact us directly.</p></div>'
        : '<div class="yp-inquiry-empty"><h3>Start with a product — or a question</h3><p>You can add styles from our product range, or continue without a selection to describe what you need.</p><a class="yp-inquiry-empty__browse" href="/collections/all">Explore products <span aria-hidden="true">↗</span></a></div>';
      showStorageWarning(container);
      return;
    }
    container.innerHTML = `<div class="yp-inquiry-items">${items.map((item, index) => {
      const quantity = Math.max(1, Math.floor(Number(item.quantity || 1)));
      const ref = item.reference && item.reference !== 'Reference pending' ? item.reference : '';
      const selection = choiceLabel(item.options || {});
      const custom = (item.customization || []).join(' · ');
      return `<article class="yp-inquiry-item" data-yp-inquiry-item data-item-index="${index}">
        <div class="yp-inquiry-item__visual">
          ${item.image ? `<img class="yp-inquiry-item__image" src="${escapeHtml(item.image)}" alt="" loading="lazy">` : '<span class="yp-inquiry-item__image yp-inquiry-item__image--empty"></span>'}
        </div>
        <div class="yp-inquiry-item__details">
          <div class="yp-inquiry-item__topline">
            ${ref ? `<span class="yp-inquiry-item__reference">${escapeHtml(ref)}</span>` : '<span class="yp-inquiry-item__reference">PRODUCT SELECTION</span>'}
          </div>
          <a class="yp-inquiry-item__title" href="${escapeHtml(item.url || '/collections/all')}">${escapeHtml(item.title || ref || 'Selected product')}</a>
          ${selection || custom ? `<div class="yp-inquiry-item__meta">${selection ? `<span>${escapeHtml(selection)}</span>` : ''}${custom ? `<span>Custom: ${escapeHtml(custom)}</span>` : ''}</div>` : ''}
        </div>
        <div class="yp-inquiry-item__notes">
          <label for="yp-inquiry-item-note-${index}">Notes <span>(optional)</span></label>
          <input id="yp-inquiry-item-note-${index}" class="yp-inquiry-item__note" data-yp-inquiry-item-note value="${escapeHtml(item.note || '')}" placeholder="Color, logo, packaging or other details">
        </div>
        <div class="yp-inquiry-item__quantity-wrap">
          <div class="yp-inquiry-item__qty-main">
            <label for="yp-inquiry-item-qty-${index}">Estimated qty</label>
            <div class="yp-inquiry-item__qty-field">
              <input id="yp-inquiry-item-qty-${index}" class="yp-inquiry-item__quantity" data-yp-inquiry-item-quantity type="number" min="1" step="1" value="${quantity}" inputmode="numeric">
              <span>pcs</span>
            </div>
          </div>
          <button type="button" class="yp-inquiry-item__remove" data-yp-inquiry-remove aria-label="Remove ${escapeHtml(item.title || 'product')} from inquiry list">Remove</button>
        </div>
      </article>`;
    }).join('')}</div>`;
    container.querySelectorAll('[data-yp-inquiry-item]').forEach((row) => {
      const index = Number(row.dataset.itemIndex);
      row.querySelector('[data-yp-inquiry-item-quantity]')?.addEventListener('change', (event) => {
        const updated = readItems();
        if (!updated[index]) { showStorageWarning(container); return; }
        const original = updated[index].quantity;
        updated[index].quantity = Math.max(1, Math.floor(Number(event.target.value || 1)));
        if (!writeItems(updated)) {
          event.target.value = original;
          showStorageWarning(container);
        }
      });
      row.querySelector('[data-yp-inquiry-item-note]')?.addEventListener('input', (event) => {
        // Persist without repainting the input; warn immediately on failure.
        const updated = readItems();
        if (!updated[index]) { showStorageWarning(container); return; }
        updated[index].note = event.target.value;
        writeItems(updated, false);
        showStorageWarning(container);
      });
      row.querySelector('[data-yp-inquiry-remove]')?.addEventListener('click', () => {
        const updated = readItems();
        if (!updated[index]) { showStorageWarning(container); return; }
        updated.splice(index, 1);
        if (!writeItems(updated)) showStorageWarning(container);
      });
    });
    showStorageWarning(container);
  };
  const quoteDetailFields = (form) => [...(form?.querySelectorAll(
    '[data-yp-quote-stage="quote"] input[name], [data-yp-quote-stage="quote"] textarea[name], [data-yp-quote-stage="quote"] select[name]'
  ) || [])].filter((field) => field.name.startsWith('contact[') &&
    field.type !== 'hidden' && field.type !== 'checkbox' && field.type !== 'radio');

  const quoteDraftNotice = (form, text, isWarning = false) => {
    const stage = form.querySelector('[data-yp-quote-stage="quote"]');
    if (!stage) return;
    const key = isWarning ? 'warning' : 'restored';
    if (stage.querySelector(`[data-yp-quote-draft-notice="${key}"]`)) return;
    const note = document.createElement('p');
    note.dataset.ypQuoteDraftNotice = key;
    note.setAttribute('role', isWarning ? 'alert' : 'status');
    note.textContent = text;
    note.style.cssText = 'margin: 0 0 16px; padding: 10px 14px; border: 1px solid #dbe3e6; border-radius: 8px; background: #f7f9f9; color: #263b49; font-size: .875rem;';
    stage.prepend(note);
  };

  const saveQuoteDraft = (form) => {
    const values = {};
    quoteDetailFields(form).forEach((field) => { values[field.name] = String(field.value || '').slice(0, 12000); });
    try {
      if (Object.values(values).some((value) => value.trim())) {
        window.sessionStorage.setItem(QUOTE_DRAFT_KEY, JSON.stringify({
          at: Date.now(), path: window.location.pathname, values
        }));
      } else {
        window.sessionStorage.removeItem(QUOTE_DRAFT_KEY);
      }
      return true;
    } catch (_error) {
      quoteDraftNotice(form, "This browser couldn't save your quote details. Please copy your message before leaving this page.", true);
      return false;
    }
  };

  const restoreQuoteDraft = (form) => {
    let draft;
    try { draft = JSON.parse(window.sessionStorage.getItem(QUOTE_DRAFT_KEY) || 'null'); }
    catch (_error) { return; }
    if (!draft || typeof draft !== 'object') return;
    const age = Date.now() - draft.at;
    if (draft.path !== window.location.pathname || !Number.isFinite(age) ||
        age < 0 || age > QUOTE_DRAFT_TTL || !draft.values || typeof draft.values !== 'object') {
      try { window.sessionStorage.removeItem(QUOTE_DRAFT_KEY); } catch (_error) { /* restricted */ }
      return;
    }
    let recovered = 0;
    quoteDetailFields(form).forEach((field) => {
      const previous = draft.values[field.name];
      if (typeof previous !== 'string' || !previous.trim() || String(field.value || '').trim()) return;
      field.value = previous;
      if (field.value === previous) recovered++;
    });
    if (recovered) quoteDraftNotice(form, 'Your previous quote details were restored in this tab. Please review them before submitting.');
  };

  const bindQuoteDraft = (form) => {
    let timer;
    const flush = () => { window.clearTimeout(timer); saveQuoteDraft(form); };
    const relevant = (event) => quoteDetailFields(form).includes(event.target);
    form.addEventListener('input', (event) => {
      if (!relevant(event)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(flush, 250);
    });
    form.addEventListener('change', (event) => { if (relevant(event)) flush(); });
    form.addEventListener('submit', flush, { capture: true });
    window.addEventListener('pagehide', flush);
  };

  const bindInquiryPage = (container) => {
    if (container.dataset.ypListBound === 'true') return;
    container.dataset.ypListBound = 'true';
    const form = container.closest('form') || document.querySelector('.yp-request-form__form');
    const quoteShell = form?.closest('.yp-b2b-shell--request-a-quote');
    const stages = [...(form?.querySelectorAll('[data-yp-quote-stage]') || [])];
    // A Shopify contact_posted URL flag is shared by all contact forms on this page.
    // Clear the shortlist only after a verified successful Request a Quote submission.
    const quoteWasSubmitted = window.YuropetContactState?.isSuccess('quote') === true;
    if (quoteWasSubmitted) {
      // Never clear either storage area on a challenge, failure or timeout.
      try { window.localStorage.removeItem(STORAGE_KEY); } catch (_error) { /* storage may be restricted */ }
      try { window.sessionStorage.removeItem(QUOTE_DRAFT_KEY); } catch (_error) { /* restricted */ }
      document.dispatchEvent(new CustomEvent('yuropet:inquiry-updated'));
    } else if (form) {
      restoreQuoteDraft(form);
      bindQuoteDraft(form);
    }
    refreshCount();

    // Both screens live in the unpublished quote page template.
    // This avoids introducing a globally published Shopify Page just to hold the list.
    const stageFromUrl = () => {
      if (quoteWasSubmitted) return 'quote';
      return new URLSearchParams(window.location.search).get('view') === 'list' ? 'list' : 'quote';
    };
    const switchStage = (stage, writeHistory = false) => {
      if (!stages.length) return;
      const target = stage === 'list' ? 'list' : 'quote';
      stages.forEach((panel) => { panel.hidden = panel.dataset.ypQuoteStage !== target; });
      form.querySelectorAll('[data-yp-quote-jump]').forEach((tab) => {
        tab.setAttribute('aria-current', tab.dataset.ypQuoteJump === target ? 'step' : 'false');
      });
      if (quoteShell) {
        const heroEyebrow = quoteShell.querySelector('[data-yp-quote-hero-eyebrow]');
        const heroTitle = quoteShell.querySelector('[data-yp-quote-hero-title]');
        const heroCopy = quoteShell.querySelector('[data-yp-quote-hero-copy]');
        if (heroEyebrow) heroEyebrow.textContent = target === 'list' ? 'YOUR PRODUCT SHORTLIST' : 'B2B QUOTE REQUEST';
        if (heroTitle) heroTitle.textContent = target === 'list' ? 'Inquiry List' : 'Request a Quote';
        if (heroCopy) heroCopy.textContent = target === 'list'
          ? 'Review the products, variants and estimated quantities you want us to discuss.'
          : 'Add your contact and destination details. We will review the request before replying.';
        const formHeading = quoteShell.querySelector('.yp-request-form__heading');
        const formIntro = quoteShell.querySelector('.yp-request-form__intro');
        if (formHeading) formHeading.textContent = target === 'list' ? 'Review your shortlist' : 'Complete your inquiry';
        if (formIntro) formIntro.textContent = target === 'list'
          ? 'Review the selection below, then continue when you are ready.'
          : 'Selected products are already attached. Add the details that help us reply.';
      }
      if (writeHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set('view', target);
        window.history.pushState({ yuropetQuoteStage: target }, '', url.pathname + url.search + url.hash);
        form.closest('.yp-request-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    form?.querySelectorAll('[data-yp-quote-jump]').forEach((button) => {
      button.addEventListener('click', () => switchStage(button.dataset.ypQuoteJump, true));
    });
    form?.querySelector('[data-yp-quote-continue]')?.addEventListener('click', () => switchStage('quote', true));
    window.addEventListener('popstate', () => switchStage(stageFromUrl()));

    const syncForm = () => {
      const stamp = `${dateStamp()}-${String(Date.now()).slice(-6)}`;
      const detail = readItems().map((item, index) => [
        `${index + 1}. ${item.reference && item.reference !== 'Reference pending' ? item.reference : 'Product selection'}`,
        item.title ? `Product: ${item.title}` : '',
        choiceLabel(item.options || {}),
        `SKU: ${item.sku || 'Not assigned'}`,
        `Qty: ${item.quantity}`,
        item.customization?.length ? `Customization: ${item.customization.join(', ')}` : '',
        item.note ? `Note: ${item.note}` : '',
        `${window.location.origin}${item.url}`
      ].filter(Boolean).join(' | ')).join('\n');
      const inquiryId = form?.querySelector('[data-yp-inquiry-id]');
      const inquiryItems = form?.querySelector('[data-yp-inquiry-items]');
      if (inquiryId) inquiryId.value = `INQ-${stamp}`;
      if (inquiryItems) inquiryItems.value = detail;
      refreshCount();
    };
    form?.addEventListener('submit', (event) => {
      readItems();
      if (storageIssue) {
        event.preventDefault();
        switchStage('list');
        showStorageWarning(container);
        container.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      syncForm();
    });
    renderInquiryList(container);
    syncForm();
    switchStage(stageFromUrl());
  };
  const bind = () => {
    document.querySelectorAll('[data-yp-product]').forEach(decorateProduct);
    document.querySelectorAll('[data-yp-inquiry-list]').forEach(bindInquiryPage);
    refreshCount();
  };
  document.addEventListener('yuropet:inquiry-updated', () => { refreshCount(); document.querySelectorAll('[data-yp-inquiry-list]').forEach(renderInquiryList); });
  document.addEventListener('DOMContentLoaded', bind);
  if (document.readyState !== 'loading') bind();
})();
